import c from 'classnames';
import { capitalize } from 'lodash';
import { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { frequencyToLabel } from '../../../common/PricingPageComponent';
import Toggle from '../../../common/Toggle';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import { FrequencyOrder, PageSettings, PricingPage } from '../../../types/pricingPage';
import { isStripePrice } from '../../../utils/products';
import { toFriendlyName } from '../../../utils/strings';
import { enumKeys } from '../../../utils/typescript';
import Info from '../../../components/Info';

const FREQUENCY_ORDER_OPTIONS: { value: string; label: string }[] = enumKeys(FrequencyOrder).map((value) => ({
    value,
    label: toFriendlyName(value),
}));

interface Props {
    page: PricingPage;
    onChangeSettings: (settings: PageSettings) => void;
}

export default function PageSettingsForm({ page, onChangeSettings }: Props) {
    const { settings } = page;
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [showCustomWording, setShowCustomWording] = useState(false);
    const handleToggleShowCustomWording = () => setShowCustomWording(!showCustomWording);
    const FREQUENCY_OPTIONS = useMemo(
        () =>
            page.plans
                .map((page) => page.product)
                .filter(isStripePrice)
                .map((product) =>
                    !!product.recurring
                        ? frequencyToLabel(product.recurring.interval, product.recurring.interval_count)
                        : ''
                )
                .filter((value) => !!value)
                .filter((value, index, array) => array.indexOf(value) === index),
        [page.plans]
    );

    const handleSave = async (values: PageSettings) => {
        setSubmitError(undefined);
        try {
            await onChangeSettings(values);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };

    return (
        <Form<PageSettings> onSubmit={handleSave} initialValues={{ ...settings }}>
            {({ handleSubmit, values }) => (
                <StyledForm onSubmit={handleSubmit}>
                    <FormSection>
                        <FormSectionHeader>Page Settings</FormSectionHeader>
                        <FormSectionBody>
                            <div className="pb-2">
                                <div className="pb-2">
                                    <Field name="monthlyYearlyToggle" type="checkbox">
                                        {(props) => (
                                            <Toggle {...props.input} label="Frequency toggle (e.g. Monthly/Yearly)" />
                                        )}
                                    </Field>
                                </div>
                                <div className="pb-2 ml-4">
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Default (month/year etc)</label>
                                        <Field name="defaultFrequency" component="select">
                                            {(props) => (
                                                <select {...props.input} className="block pl-3 pr-10 select">
                                                    <option>-</option>
                                                    {FREQUENCY_OPTIONS.map((option) => (
                                                        <option key={option} value={option}>
                                                            {capitalize(option)}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="mb-2">
                                        <Field name="promotedFrequency" type="checkbox">
                                            {(props) => (
                                                <Toggle {...props.input} label="Promotion">
                                                    <span className="ml-2 inline bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full border border-green-400">
                                                        {settings.promotedFrequencyWording || 'Save 20%'}
                                                    </span>
                                                </Toggle>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Frequency Order</label>
                                        <Field name="frequencyOrder" component="select">
                                            {(props) => (
                                                <select {...props.input} className="block pl-3 pr-10 select">
                                                    {FREQUENCY_ORDER_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </Field>
                                    </div>
                                    <div className={c({ hidden: !values.promotedFrequency })}>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600">Promo text</label>
                                            <Field name="promotedFrequencyWording" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="Save 20%"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="pb-2">
                                <Field name="displayMonthlyPrice" type="checkbox">
                                    {(props) => (
                                        <Toggle
                                            {...props.input}
                                            label={`Show "X/mo billed yearly" for yearly products`}
                                        />
                                    )}
                                </Field>
                            </div>
                            <div className="pb-2">
                                <Field name="promotedPlan" type="checkbox">
                                    {(props) => <Toggle {...props.input} label="Promoted plan" />}
                                </Field>
                            </div>
                            <div className="pb-2">
                                <Field name="planPicker.enabled" type="checkbox">
                                    {(props) => (
                                        <Toggle
                                            {...props.input}
                                            label={`Display "Current Plan" and "Upgrade" buttons`}
                                        />
                                    )}
                                </Field>
                                <div className={c({ hidden: !values.planPicker.enabled })}>
                                    <div className="pb-2 ml-4">
                                        <label className="text-sm text-gray-600">Plan Picker Settings</label>
                                        <Field name="planPicker.proration" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle {...props.input} label={`Proration`} />
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="planPicker.disableOneOff" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle {...props.input} label={`Disable One Off Products`} />
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="planPicker.disableZeroPrice" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle {...props.input} label={`Disable Free ($0) Products`} />
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="planPicker.confirmChanges" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle
                                                        {...props.input}
                                                        label={`Confirm changes`}
                                                        info={`Shows a popup asking "Are you sure?" when a customer upgrades or downgrades`}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="planPicker.scheduleUpgrades" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle
                                                        {...props.input}
                                                        label={`Schedule changes for Upgrades`}
                                                        info={`Instead of applying upgrades immediately, this option schedules any changes to happen when the next renewal is due. Upgrades are changes that have the same billing period (monthly) but a higher price.`}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                        <Field name="planPicker.scheduleDowngrades" type="checkbox">
                                            {(props) => (
                                                <div className="mb-2">
                                                    <Toggle
                                                        {...props.input}
                                                        label={`Schedule changes for Downgrades`}
                                                        info={`Instead of applying downgrades immediately, this option schedules any changes to happen when the next renewal is due. Downgrades are changes that have the same billing period (monthly) but a lower price.`}
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            </div>
                        </FormSectionBody>
                    </FormSection>
                    <FormSection>
                        <FormSectionHeader>Custom Wording</FormSectionHeader>
                        <FormSectionBody>
                            <h4 className="text-primary-normal text-left">
                                <button
                                    onClick={handleToggleShowCustomWording}
                                    className="ml-4 underline mb-8 text-gray-500"
                                >
                                    {showCustomWording ? 'Hide' : 'Show'} custom wording settings
                                </button>
                            </h4>
                            <div className={c({ hidden: !showCustomWording })}>
                                <div className="pb-4">
                                    <label className="text-sm text-gray-600 pb-2">Abbreviations</label>
                                    <div className="ml-4">
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Week</label>
                                            <Field name="customWording.weekAbbreviation" type="text">
                                                {(props) => (
                                                    <input {...props.input} className="input ml-2" placeholder="week" />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Month</label>
                                            <Field name="customWording.monthAbbreviation" type="text">
                                                {(props) => (
                                                    <input {...props.input} className="input ml-2" placeholder="mo" />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Months (plural)</label>
                                            <Field name="customWording.monthsAbbreviation" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="months"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Year</label>
                                            <Field name="customWording.yearAbbreviation" type="text">
                                                {(props) => (
                                                    <input {...props.input} className="input ml-2" placeholder="year" />
                                                )}
                                            </Field>
                                        </div>

                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Weekly</label>
                                            <Field name="customWording.weeklyAbbreviation" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="Weekly"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Monthly</label>
                                            <Field name="customWording.monthlyAbbreviation" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="Monthly"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Quarterly</label>
                                            <Field name="customWording.quarterlyAbbreviation" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="Quarterly"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="pb-2">
                                            <label className="text-sm text-gray-600 pb-2">Yearly</label>
                                            <Field name="customWording.yearlyAbbreviation" type="text">
                                                {(props) => (
                                                    <input
                                                        {...props.input}
                                                        className="input ml-2"
                                                        placeholder="Yearly"
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                <div className="pb-2">
                                    <label className="text-sm text-gray-600">Users</label>
                                    <Field name="customWording.users" type="text">
                                        {(props) => (
                                            <input {...props.input} className="input ml-2" placeholder="Users" />
                                        )}
                                    </Field>
                                </div>
                                <div className="pb-2">
                                    <label className="text-sm text-gray-600">Tiered Pricing (e.g. Up to X Users)</label>
                                    <Field name="customWording.upToXUsers" type="text">
                                        {(props) => (
                                            <input {...props.input} className="input ml-2" placeholder="Up to" />
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <div className="mr-0 ml-auto">{submitError && <Error message={submitError} />}</div>{' '}
                            <AutoSave debounce={1000} save={handleSave} />
                        </FormSectionBody>
                    </FormSection>
                </StyledForm>
            )}
        </Form>
    );
}
